import { useQuery } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../utils/trpc'
import type { CrmProvider, FieldMapping } from '../types'

interface DataMappingStepProps {
	provider: CrmProvider
	connectionId?: string
	fieldMappings: FieldMapping[]
	onChange: (mappings: FieldMapping[]) => void
}

const transforms = [
	{ value: '', label: 'None' },
	{ value: 'toLowerCase', label: 'To Lowercase' },
	{ value: 'toUpperCase', label: 'To Uppercase' },
	{ value: 'trim', label: 'Trim Whitespace' },
	{ value: 'splitCommas', label: 'Split by Commas' },
]

export function DataMappingStep({ provider, connectionId, fieldMappings, onChange }: DataMappingStepProps) {
	const { organizationId } = useParams<{ organizationId: string }>()

	const {
		data: crmFields,
		isLoading: isLoadingCrmFields,
		error: crmFieldsError,
	} = useQuery({
		...trpc.admin.integrations.crm.getAvailableFields.queryOptions({
			provider,
			organizationId,
			connectionId,
		}),
		enabled: !!provider,
	})

	const {
		data: viaPeopleFields,
		isLoading: isLoadingVpFields,
		error: vpFieldsError,
	} = useQuery(trpc.admin.integrations.crm.getViaPeopleFields.queryOptions())

	const isLoading = isLoadingCrmFields || isLoadingVpFields

	// Initialize/merge mappings from required + optional viaPeople fields when data arrives
	useEffect(() => {
		if (viaPeopleFields) {
			const allFields = [...viaPeopleFields.required, ...viaPeopleFields.optional]
			const existingByTarget = new Map(fieldMappings.map((m) => [m.viaPeopleField, m]))

			// Only update if there are missing fields (avoids infinite loop)
			const hasMissing = allFields.some((f) => !existingByTarget.has(f))

			if (hasMissing || fieldMappings.length === 0) {
				// Preserve user-added custom mappings (any target not in the preset list); without
				// this, reloading an integration that didn't fill every optional preset would drop
				// the custom rows from React state and the next save would delete them from the DB.
				const presetSet = new Set<string>(allFields)
				const customMappings = fieldMappings.filter((m) => !presetSet.has(m.viaPeopleField))
				const mergedMappings = [
					...allFields.map(
						(field) => existingByTarget.get(field) || { crmField: '', viaPeopleField: field, transform: '' },
					),
					...customMappings,
				]
				onChange(mergedMappings)
			}
		}
	}, [viaPeopleFields, fieldMappings, onChange])

	const handleCrmFieldChange = (index: number, crmField: string) => {
		const updated = [...fieldMappings]
		updated[index] = { ...updated[index], crmField }
		onChange(updated)
	}

	const handleTransformChange = (index: number, transform: string) => {
		const updated = [...fieldMappings]
		updated[index] = { ...updated[index], transform }
		onChange(updated)
	}

	const handleViaPeopleFieldChange = (index: number, viaPeopleField: string) => {
		const updated = [...fieldMappings]
		updated[index] = { ...updated[index], viaPeopleField }
		onChange(updated)
	}

	const handleAddCustomMapping = () => {
		onChange([...fieldMappings, { crmField: '', viaPeopleField: '', transform: '' }])
	}

	const handleRemoveMapping = (index: number) => {
		const updated = [...fieldMappings]
		updated.splice(index, 1)
		onChange(updated)
	}

	if (isLoading) {
		return <SpinnerWithBackground backgroundClass='opacity-100' />
	}

	if (vpFieldsError || crmFieldsError) {
		return (
			<div className='p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700'>
				<Text variant='small' weight='semibold'>
					Error loading data mapping options
				</Text>
				<Text variant='xsmall'>{(vpFieldsError as any)?.message || (crmFieldsError as any)?.message}</Text>
			</div>
		)
	}

	const requiredFields: readonly string[] = viaPeopleFields?.required ?? []
	const optionalFields: readonly string[] = viaPeopleFields?.optional ?? []
	const hasPresetFields = crmFields && crmFields.length > 0

	return (
		<div className='flex flex-col gap-6'>
			<div>
				<Text variant='medium' weight='semibold' className='mb-1'>
					Data Mapping
				</Text>
				<Text variant='small' className='text-neutral-600'>
					{hasPresetFields
						? 'Map CRM fields to viaPeople fields. Required fields are marked with *.'
						: 'Enter the field paths from your webhook payload (e.g., data.dealName). Required fields are marked with *.'}
				</Text>
				{!hasPresetFields && provider === 'salesforce' && !isLoading && (
					<div className='mt-2 p-2 bg-warning-50 border border-warning-200 rounded text-warning-800 text-sm'>
						Warning: Could not load Salesforce fields. Showing manual entry mode.
						<br />
						{(crmFieldsError as any)?.message && <>Error: {(crmFieldsError as any).message}</>}
					</div>
				)}
			</div>

			<div className='border border-neutral-200 rounded-lg overflow-hidden'>
				{/* Header */}
				<div className='grid grid-cols-12 gap-2 px-4 py-3 bg-neutral-100 border-b border-neutral-200'>
					<div className='col-span-11 grid grid-cols-11 gap-2'>
						<div className='col-span-4'>
							<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase'>
								viaPeople Field
							</Text>
						</div>
						<div className='col-span-1 flex items-center justify-center'>
							<Text variant='xsmall' className='text-neutral-400'>
								←
							</Text>
						</div>
						<div className='col-span-3'>
							<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase'>
								{hasPresetFields ? 'CRM Source Field' : 'Payload Field Path'}
							</Text>
						</div>
						<div className='col-span-3'>
							<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase'>
								Transform
							</Text>
						</div>
					</div>
					<div className='col-span-1'></div>
				</div>

				{/* Rows */}
				{fieldMappings.map((mapping, index) => {
					const isRequired = requiredFields.includes(mapping.viaPeopleField)
					const isPreset = isRequired || optionalFields.includes(mapping.viaPeopleField as any)

					return (
						<div
							// Fall back to index when viaPeopleField is empty (e.g., a freshly added
							// custom row before the user has typed). Two empty rows would otherwise
							// collide and React would warn / mis-key the inputs.
							key={mapping.viaPeopleField || `__custom_${index}`}
							className='grid grid-cols-12 gap-2 px-4 py-3 border-b border-neutral-100 last:border-b-0 items-center bg-white hover:bg-neutral-50'>
							<div className='col-span-11 grid grid-cols-11 gap-2 items-center'>
								<div className='col-span-4'>
									{isPreset ? (
										<Text variant='small' weight={isRequired ? 'semibold' : 'regular'}>
											{mapping.viaPeopleField}
											{isRequired && <span className='text-danger-600 ml-0.5'>*</span>}
										</Text>
									) : (
										<input
											type='text'
											value={mapping.viaPeopleField}
											onChange={(e) => handleViaPeopleFieldChange(index, e.target.value)}
											placeholder='Custom Field Key'
											className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
										/>
									)}
								</div>
								<div className='col-span-1 flex items-center justify-center'>
									<Text variant='xsmall' className='text-neutral-300'>
										←
									</Text>
								</div>
								<div className='col-span-3'>
									{hasPresetFields ? (
										<select
											value={mapping.crmField}
											onChange={(e) => handleCrmFieldChange(index, e.target.value)}
											className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'>
											<option value=''>Select field...</option>
											{crmFields?.map((field) => (
												<option key={field.path} value={field.path}>
													{field.label} ({field.path})
												</option>
											))}
										</select>
									) : (
										<input
											type='text'
											value={mapping.crmField}
											onChange={(e) => handleCrmFieldChange(index, e.target.value)}
											placeholder='e.g., data.dealName'
											className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
										/>
									)}
								</div>
								<div className='col-span-3'>
									<select
										value={mapping.transform || ''}
										onChange={(e) => handleTransformChange(index, e.target.value)}
										className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'>
										{transforms.map((t) => (
											<option key={t.value} value={t.value}>
												{t.label}
											</option>
										))}
									</select>
								</div>
							</div>
							<div className='col-span-1 flex justify-center'>
								{!isPreset && (
									<button
										type='button'
										onClick={() => handleRemoveMapping(index)}
										className='text-neutral-400 hover:text-danger-600 transition-colors'
										title='Remove mapping'>
										<span className='sr-only'>Delete</span>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											fill='none'
											viewBox='0 0 24 24'
											strokeWidth={1.5}
											stroke='currentColor'
											className='w-5 h-5'
											aria-hidden='true'>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												d='M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0'
											/>
										</svg>
									</button>
								)}
							</div>
						</div>
					)
				})}
			</div>

			<div>
				<Button variant='secondary' size='small' onClick={handleAddCustomMapping}>
					+ Add Custom Mapping
				</Button>
			</div>
		</div>
	)
}
